import * as THREE from 'three';
import type { InteractableData } from './Interactable';
import type { PlayerController } from '../player/PlayerController';

export class InteractionSystem {
  private interactables: Map<string, InteractableData>;
  private highlighted: THREE.Object3D | null = null;
  private originalEmissive = new Map<string, { color: THREE.Color; intensity: number }>();
  current: InteractableData | null = null;

  constructor(interactables: Map<string, InteractableData>) {
    this.interactables = interactables;
  }

  update(player: PlayerController): InteractableData | null {
    const rc = player.getLookRaycaster();
    const meshes: THREE.Object3D[] = [];
    for (const i of this.interactables.values()) {
      if (i.mesh.visible) meshes.push(i.mesh);
    }
    const hits = rc.intersectObjects(meshes, true);
    let found: InteractableData | null = null;
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object;
      while (o) {
        if (o.userData.interactId) {
          found = this.interactables.get(o.userData.interactId) ?? null;
          break;
        }
        // 親に付いている場合
        for (const [id, data] of this.interactables) {
          if (data.mesh === o || data.mesh.children.includes(o as THREE.Object3D)) {
            found = data;
            void id;
            break;
          }
        }
        if (found) break;
        o = o.parent;
      }
      if (found) break;
    }

    // フォールバック: 視線前方半球の最近傍
    if (!found) {
      const pos = player.camera.position;
      const look = new THREE.Vector3(0, 0, -1).applyQuaternion(player.camera.quaternion);
      let best = Infinity;
      for (const data of this.interactables.values()) {
        if (!data.mesh.visible) continue;
        const wp = new THREE.Vector3();
        data.mesh.getWorldPosition(wp);
        const dist = pos.distanceTo(wp);
        if (dist > 3.4 || dist >= best) continue;
        const dir = wp.clone().sub(pos).normalize();
        // 正面〜側面まで拾う（真後ろは除外）
        if (dir.dot(look) > 0.15) {
          best = dist;
          found = data;
        }
      }
    }

    this.setHighlight(found?.mesh ?? null);
    this.current = found;
    return found;
  }

  private setHighlight(obj: THREE.Object3D | null): void {
    if (this.highlighted === obj) return;
    if (this.highlighted) this.clearHighlight(this.highlighted);
    this.highlighted = obj;
    if (obj) this.applyHighlight(obj);
  }

  private applyHighlight(obj: THREE.Object3D): void {
    obj.traverse((c) => {
      const mesh = c as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const m = mat as THREE.MeshStandardMaterial;
        if (!m.emissive) continue;
        const key = mesh.uuid + m.uuid;
        if (!this.originalEmissive.has(key)) {
          this.originalEmissive.set(key, {
            color: m.emissive.clone(),
            intensity: m.emissiveIntensity ?? 0,
          });
        }
        m.emissive = new THREE.Color(0xc9a84c);
        m.emissiveIntensity = 0.35;
      }
    });
  }

  private clearHighlight(obj: THREE.Object3D): void {
    obj.traverse((c) => {
      const mesh = c as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const m = mat as THREE.MeshStandardMaterial;
        if (!m.emissive) continue;
        const key = mesh.uuid + m.uuid;
        const orig = this.originalEmissive.get(key);
        if (orig) {
          m.emissive.copy(orig.color);
          m.emissiveIntensity = orig.intensity;
        }
      }
    });
  }

  get(id: string): InteractableData | undefined {
    return this.interactables.get(id);
  }
}
